import React from "react";
import Modal from "../Modal";
import changes from "../../changes";
import { PERMANENTLY_EXCLUDED_FIELDS } from "./utils";
import { getIndexFieldsOrdered } from "./utils";
import PreviewFormModalHeader from "./PreviewFormModalHeader";
import PreviewFormModalSummary from "./PreviewFormModalSummary";
import PreviewFormModalBody from "./PreviewFormModalBody";
import PreviewFormModalFooter from "./PreviewFormModalFooter";

function getPreviewData(object, originalObject, excludeFields, schema) {
  const data = object ?? {};
  const original = originalObject ?? {};
  const isEditing = object?.id || originalObject?.id;
  const excludedSet = new Set([
    ...PERMANENTLY_EXCLUDED_FIELDS,
    ...(Array.isArray(excludeFields) ? excludeFields : []),
  ]);

  const changedValues = changes(original, data);
  const changedKeysSet = new Set(Object.keys(changedValues));

  const schemaFields = schema?.fields || {};
  const hasSchemaFields = Object.keys(schemaFields).length > 0;

  let visibleKeys;
  if (hasSchemaFields) {
    const ordered = getIndexFieldsOrdered(schema);
    const fallbackKeys = Object.keys(schemaFields);
    const allowed = (key) => {
      const config = schemaFields[key];
      if (!config) return false;
      if (excludedSet.has(key)) return false;
      if (
        config.write === false ||
        config._write === false ||
        config._io === false
      )
        return false;
      return true;
    };
    // Use all schema fields for preview; order by _index when present, then rest
    if (ordered.length > 0) {
      const orderedAllowed = ordered.filter(
        (k) => fallbackKeys.includes(k) && allowed(k),
      );
      const rest = fallbackKeys.filter(
        (k) => !ordered.includes(k) && allowed(k),
      );
      visibleKeys = [...orderedAllowed, ...rest];
    } else {
      visibleKeys = fallbackKeys.filter(allowed);
    }
  } else {
    const allKeys = [
      ...new Set([...Object.keys(data), ...Object.keys(original)]),
    ];
    visibleKeys = allKeys.filter((key) => !excludedSet.has(key));
  }

  const modifiedCount = visibleKeys.filter(
    (key) => changedKeysSet.has(key) || (key in original && !(key in data)),
  ).length;

  return {
    data,
    original,
    visibleKeys,
    changedKeysSet,
    modifiedCount,
    isEditing,
  };
}

function PreviewFormModal({
  isOpen,
  schema = {},
  object = {},
  originalObject,
  title = "Preview",
  onClose,
  onConfirm,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "primary",
  icon = "bi bi-eye",
  description = "Kindly verify the details below before proceeding.",
  excludeFields = [],
  schemas = [],
  progress = false,
}) {
  const {
    data,
    original,
    visibleKeys,
    changedKeysSet,
    modifiedCount,
    isEditing,
  } = getPreviewData(object, originalObject, excludeFields, schema);

  const schemaFields = schema?.fields || {};

  return (
    <Modal isOpen={isOpen} size="lg">
      <div className="d-flex flex-column overflow-hidden rounded-3">
        <PreviewFormModalHeader
          title={title}
          description={description}
          icon={icon}
          type={type}
        />
        <PreviewFormModalSummary
          totalFields={visibleKeys.length}
          modifiedCount={modifiedCount}
        />
        <PreviewFormModalBody
          visibleKeys={visibleKeys}
          data={data}
          original={original}
          isEditing={isEditing}
          schemaFields={schemaFields}
          changedKeysSet={changedKeysSet}
          schemas={schemas}
          progress={progress}
        />
        <PreviewFormModalFooter
          cancelText={cancelText}
          confirmText={confirmText}
          type={type}
          progress={progress}
          onClose={onClose}
          onConfirm={onConfirm}
        />
      </div>
    </Modal>
  );
}

export default PreviewFormModal;
